/**
 * Pinned-top drift bucket for strichliste-side-created articles that
 * have no admin-side category. Yellow-tinted to draw the eye; each
 * article inside is a draggable source (DRAG_ARTICLE) so the operator
 * can drop it onto any category's group, placeholder, or the category
 * row itself (Ungrouped drop target).
 */

import { useRef } from "react";
import { useDrag } from "react-dnd";
import { Card, Space, Tag, Typography } from "antd";
import { DragOutlined } from "@ant-design/icons";

import type { ChainRow } from "../api/client";
import {
  DRAG_ARTICLE,
  type ArticleDragItem,
} from "./CatalogDnd";

const { Title, Text } = Typography;

interface Props {
  rows: ChainRow[];
  onSelectArticle: (chainRootId: number) => void;
}

export const UncategorisedBucket = ({ rows, onSelectArticle }: Props) => {
  if (rows.length === 0) return null;
  return (
    <Card
      size="small"
      id="uncategorised"
      style={{
        marginBottom: 12,
        background: "var(--ant-color-warning-bg, #fffbe6)",
        borderColor: "var(--ant-color-warning-border, #ffe58f)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={5} style={{ margin: 0 }}>
          — No Category —
        </Title>
        <Tag color="warning">{rows.length} article{rows.length === 1 ? "" : "s"}</Tag>
      </div>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Created via strichliste's API with no admin category. Drag into a category, group,
        or placeholder to clear the drift — or click to open the detail pane.
      </Text>
      <Space wrap size={[8, 8]}>
        {rows.map((row) => (
          <DriftArticleChip
            key={row.chain_root_id}
            row={row}
            onSelect={() => onSelectArticle(row.chain_root_id)}
          />
        ))}
      </Space>
    </Card>
  );
};

function DriftArticleChip({
  row,
  onSelect,
}: {
  row: ChainRow;
  onSelect: () => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [{ isDragging }, drag] = useDrag<ArticleDragItem, void, { isDragging: boolean }>(
    () => ({
      type: DRAG_ARTICLE,
      item: {
        kind: DRAG_ARTICLE,
        chainRootId: row.chain_root_id,
        fromGroupId: null,
        fromCategoryId: null,
      },
      collect: (m) => ({ isDragging: m.isDragging() }),
    }),
    [row.chain_root_id],
  );
  drag(ref);
  return (
    <span
      ref={ref}
      onClick={onSelect}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        background: "var(--ant-color-bg-container)",
        border: "1px solid var(--ant-color-warning-border, #ffe58f)",
        borderRadius: 14,
        cursor: "grab",
        opacity: isDragging ? 0.4 : 1,
        userSelect: "none",
        fontSize: 13,
      }}
    >
      <DragOutlined style={{ color: "var(--ant-color-text-tertiary)" }} />
      {row.name}
    </span>
  );
}
